import { useEffect, useState } from "react";
import { toDate } from "./format";
import { currentPlan } from "./plans";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Vencimiento de un envío para un participante, según SU plan vigente: el mismo cálculo que
// firestore.rules, storage.rules y las funciones (functions/vencimientos.js).
// order.vence = { <uid>: { gratis, pago } } lo escribe el servidor.
//   vencido:  ya no lo ve (sin archivos, visor ni chat).
//   venceEl:  fecha (ms) en que vence en el plan gratis (null en planes pagos).
//   venceEn:  ms que faltan para esa fecha (null en planes pagos).
//   borrada:  sus archivos ya se borraron.
export function orderExpiry(order, uid, planData, now = Date.now()) {
  const plan = currentPlan(planData || {}, now).plan;
  const entry = order?.vence?.[uid];
  const borrada = !!order?.borrada;
  // Órdenes sin fechas (anteriores a la migración): se muestran como siempre.
  if (!entry?.gratis) return { plan, vencido: false, venceEl: null, venceEn: null, borrada };
  const limit = toDate(plan === "gratis" ? entry.gratis : entry.pago)?.getTime() ?? 0;
  const vencido = now >= limit;
  const gratis = plan === "gratis" && !vencido;
  return { plan, vencido, venceEl: gratis ? limit : null, venceEn: gratis ? limit - now : null, borrada };
}

// Plazo restante, redondeado hacia arriba: "en 45 min", "en 23 h", "en 2 días".
function remaining(ms) {
  const minutes = Math.max(1, Math.ceil(ms / MINUTE));
  if (minutes < 60) return `en ${minutes} min`;
  const hours = Math.ceil(ms / HOUR);
  if (hours <= 24) return `en ${hours} h`;
  return `en ${Math.ceil(ms / DAY)} días`;
}

// Lista de órdenes: "Vence en 23 h".
export function formatRemaining(ms) {
  return ms == null ? "" : `Vence ${remaining(ms)}`;
}

// Detalle de la orden: "Vence el 1/11 a las 11:23 · en 23 h".
export function formatExpiry(venceEl, now = Date.now()) {
  if (venceEl == null) return "";
  const date = new Date(venceEl);
  const hora = date.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `Vence el ${date.getDate()}/${date.getMonth() + 1} a las ${hora} · ${remaining(venceEl - now)}`;
}

// Hora actual que se actualiza sola (por defecto cada minuto), para que el plazo no quede congelado.
export function useNow(intervalMs = MINUTE) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// ¿La puede abrir quien mira? Ni vencida para él ni con los archivos ya borrados.
export function canOpen(order, uid, planData, now = Date.now()) {
  const { vencido, borrada } = orderExpiry(order, uid, planData, now);
  return !vencido && !borrada;
}

// Las borradas salen de Recibidas, Enviadas e Inicio y se ven solo en "Vencidos".
export function splitDeleted(orders) {
  return { active: orders.filter((o) => !o.borrada), deleted: orders.filter((o) => o.borrada) };
}

// Globito de Historial y "Por revisar" de Inicio: recibidas en "Recibido" que todavía puedo abrir.
export function pendingCount(orders, uid, planData, now = Date.now()) {
  return orders.filter((o) => o.recipientUid === uid && o.estado === "recibido" && canOpen(o, uid, planData, now)).length;
}
